import { useState, useCallback, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from './useAuth';

export type UserRole = 'admin' | 'moderator' | 'user' | 'manager';


export function useUserRole() {
  const { user } = useAuth();
  const [role, setRole] = useState<UserRole>('user');
  const [loading, setLoading] = useState(true);

  const fetchRole = useCallback(async () => {
    if (!user?.id) {
      setRole('user');
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      const { data, error } = await supabase
        .from('user_roles')
        .select('role')
        .eq('user_id', user.id)
        .maybeSingle();

      if (error && error.code !== 'PGRST116') throw error; // PGRST116 = no rows returned
      
      setRole((data?.role as UserRole) || 'user');
    } catch (err) {
      console.error('Error fetching user role:', err);
      setRole('user');
    } finally {
      setLoading(false);
    }
  }, [user?.id]);

  useEffect(() => {
    fetchRole();
  }, [fetchRole]);

  const isManager = role === 'admin' || role === 'manager';
  const isAdmin = role === 'admin';
  const isModerator = role === 'moderator' || isManager;
  const isUser = role === 'user';

  return {
    role,
    loading,
    isManager,
    isAdmin,
    isModerator,
    isUser,
    fetchRole,
  };
}
